import React, { useState } from 'react';
import { Server, Bot, Palette, Download, Key, BarChart3, Clock, Send, Users, Activity, Globe, Bell, SlidersHorizontal, BookOpen } from 'lucide-react';
import { MCPServerSettings } from './MCPServerSettings';
import { LLMProviderSettings } from './LLMProviderSettings';
import { ClusterSettings } from './ClusterSettings';
import { AppearanceSettings } from './AppearanceSettings';
import { ImportExportSettings } from './ImportExportSettings';
import { VaultSettings } from './VaultSettings';
import { TokenUsageStats } from './TokenUsageStats';
import { ScheduledQueriesSettings } from './ScheduledQueriesSettings';
import { TelegramSettings } from './TelegramSettings';
import { AlertSettings } from './AlertSettings';
import { KnowledgeBaseSettings } from './KnowledgeBaseSettings';
import { UserManagement } from '@/components/admin/UserManagement';
import { PromptCustomizationPanel } from '@/components/admin/PromptCustomizationPanel';
import { AnalyticsDashboard } from '@/components/analytics/AnalyticsDashboard';
import { useAuthStore } from '@/stores/authStore';
import { clsx } from 'clsx';
import { useTranslation } from '@/i18n';

type SettingsTab = 'mcp' | 'llm' | 'clusters' | 'vault' | 'stats' | 'analytics' | 'scheduled' | 'alerts' | 'knowledge' | 'telegram' | 'appearance' | 'import-export' | 'users' | 'dispatch';

export const SettingsInterface: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  // Restore active tab from URL hash (e.g., #settings/vault → 'vault')
  const [activeTab, setActiveTab] = useState<SettingsTab>(() => {
    const parts = window.location.hash.replace('#', '').split('/');
    const hashTab = parts[1] as SettingsTab | undefined;
    const validTabs: SettingsTab[] = ['mcp', 'llm', 'clusters', 'vault', 'stats', 'analytics', 'scheduled', 'alerts', 'knowledge', 'telegram', 'appearance', 'import-export', 'users', 'dispatch'];
    if (hashTab && validTabs.includes(hashTab)) {
      // Don't restore admin-only tabs for non-admin users
      const adminOnly: SettingsTab[] = ['mcp', 'clusters', 'vault', 'users', 'dispatch'];
      if (!isAdmin && adminOnly.includes(hashTab)) return 'llm';
      return hashTab;
    }
    return isAdmin ? 'mcp' : 'llm';
  });

  // Sync tab changes to URL hash
  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab);
    window.history.replaceState(null, '', `#settings/${tab}`);
  };

  const tabs: Array<{ id: SettingsTab; label: string; icon: React.FC<any>; adminOnly?: boolean }> = [
    { id: 'mcp', label: t.settings.mcpServers, icon: Server, adminOnly: true },
    { id: 'llm', label: t.settings.llmProviders, icon: Bot },
    { id: 'clusters', label: (t as any).settings?.clusters || 'Clusters', icon: Globe, adminOnly: true },
    { id: 'vault', label: t.settings.vault || 'Vault Secrets', icon: Key, adminOnly: true },
    { id: 'stats', label: t.settings.tokenUsage || 'Token Usage', icon: BarChart3 },
    { id: 'analytics', label: (t as any).analytics?.title || 'Analytics', icon: Activity },
    { id: 'scheduled', label: (t as any).scheduledQueries?.title || 'Scheduled Queries', icon: Clock },
    { id: 'alerts', label: (t as any).alerts?.title || 'Alert Rules', icon: Bell },
    { id: 'knowledge', label: t.knowledge.title, icon: BookOpen },
    { id: 'telegram', label: (t as any).telegram?.title || 'Telegram', icon: Send },
    { id: 'appearance', label: t.settings.appearance, icon: Palette },
    { id: 'import-export', label: t.settings.importExport, icon: Download },
    { id: 'users', label: (t as any).admin?.userManagement || 'Users', icon: Users, adminOnly: true },
    { id: 'dispatch', label: (t as any).dispatch?.title || 'Prompt Customization', icon: SlidersHorizontal, adminOnly: true },
  ];

  const visibleTabs = tabs.filter((tab) => !tab.adminOnly || isAdmin);

  const renderContent = () => {
    switch (activeTab) {
      case 'mcp':
        return <MCPServerSettings />;
      case 'llm':
        return <LLMProviderSettings />;
      case 'clusters':
        return <ClusterSettings />;
      case 'vault':
        return <VaultSettings />;
      case 'stats':
        return <TokenUsageStats />;
      case 'analytics':
        return <AnalyticsDashboard />;
      case 'scheduled':
        return <ScheduledQueriesSettings />;
      case 'alerts':
        return <AlertSettings />;
      case 'knowledge':
        return <KnowledgeBaseSettings />;
      case 'telegram':
        return <TelegramSettings />;
      case 'appearance':
        return <AppearanceSettings />;
      case 'import-export':
        return <ImportExportSettings />;
      case 'users':
        return isAdmin ? <UserManagement /> : null;
      case 'dispatch':
        return isAdmin ? <PromptCustomizationPanel /> : null;
      default:
        return null;
    }
  };

  return (
    <div className="flex h-full bg-white dark:bg-gray-900">
      {/* Sidebar */}
      <div className="w-72 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
        <div className="p-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t.settings.title}
          </h2>
          <nav className="space-y-1">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={clsx(
                    'w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  )}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};
